<script setup lang="ts">
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  type CSSProperties,
} from 'vue'

const props = withDefaults(
  defineProps<{
    placement?: 'right' | 'left' | 'center'
    offsetX?: number
    offsetY?: number
  }>(),
  {
    placement: 'right',
    offsetX: 0,
    offsetY: 0,
  },
)

const isOpen = ref(false)
const trigger = ref<HTMLElement | null>(null)

function toggle() {
  isOpen.value = !isOpen.value
}

function close() {
  isOpen.value = false
}

function onClickOutside(event: MouseEvent) {
  if (!trigger.value?.contains(event.target as Node)) {
    close()
  }
}

onMounted(() => {
  document.addEventListener('click', onClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onClickOutside)
})

const popoverStyle = computed(() => {
  const yOffset = 8 + props.offsetY

  const base = {
    position: 'absolute',
    top: `calc(100% + ${yOffset}px)`,
    minWidth: '180px',
    zIndex: 1000,
  }

  switch (props.placement) {
    case 'left':
      return { ...base, left: 0, transform: `translateX(${props.offsetX}px)` }
    case 'center':
      return {
        ...base,
        left: '50%',
        transform: `translateX(calc(-50% + ${props.offsetX}px))`,
      }
    case 'right':
    default:
      return { ...base, right: 0, transform: `translateX(${props.offsetX}px)` }
  }
}) as unknown as CSSProperties
</script>

<template>
  <div class="popover-wrapper">
    <div ref="trigger" class="popover-trigger" @click="toggle">
      <slot name="trigger" />
    </div>

    <div
      v-if="isOpen"
      ref="popover"
      class="popover-content"
      :style="popoverStyle"
    >
      <slot name="content" />
    </div>
  </div>
</template>

<style scoped>
.popover-wrapper {
  position: relative;
  display: inline-block;
}

.popover-trigger {
  cursor: pointer;
}

.popover-content {
  border-radius: 8px;
  background: var(--color-white);
  box-shadow:
    0 2px 4px 0 rgba(0, 0, 0, 15%),
    0 1px 24px 0 rgba(0, 0, 0, 15%);
}
</style>
